import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { User, Session } from '@supabase/supabase-js';
import { DebugLogger } from '@/utils/debugLogger';
import { isDemoMode, DEMO_USER, createDemoSession } from '@/utils/demoMode';
import { captureMessage } from '@/lib/sentry';

import { AuthContext, UserRole, UserProfile } from './authContext';
import { fetchUserRole, fetchUserProfile } from './authDataService';
import { signUp as authSignUp, signIn as authSignIn, signOut as authSignOut } from './authOperations';

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const setters = { setUser, setSession, setUserRole, setProfile, setLoading, navigate };

  useEffect(() => {
    let mounted = true;
    
    // Demo mode bypass
    if (isDemoMode()) {
      if (import.meta.env.PROD) {
        captureMessage('SECURITY: Demo mode activation attempted in production', 'error');
      }
      setSession(createDemoSession() as Session);
      setUser(DEMO_USER as User);
      setUserRole('admin_entreprise');
      setProfile({ first_name: 'Compte', last_name: 'Démo' });
      setLoading(false);
      return;
    }
    
    // Auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!mounted) return;
        
        DebugLogger.log('auth', `Auth state changed: ${event}`, {
          hasSession: !!session,
          hasUser: !!session?.user
        });
        
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          setTimeout(() => {
            if (mounted) {
              const profileFromMetadata = {
                first_name: session.user.user_metadata?.first_name,
                last_name: session.user.user_metadata?.last_name
              };
              
              Promise.all([
                fetchUserRole(session.user.id),
                fetchUserProfile(session.user.id)
              ]).then(([role, dbProfile]) => {
                if (mounted) {
                  setUserRole(role);
                  setProfile(dbProfile || profileFromMetadata);
                  setLoading(false);
                }
              }).catch(() => {
                if (mounted) {
                  setUserRole('apprenant_particulier');
                  setProfile(profileFromMetadata);
                  setLoading(false);
                }
              });
            }
          }, 0);
        } else {
          setUserRole(null);
          setProfile(null);
          setLoading(false);
        }
      }
    );

    // Initial session check
    const initializeAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!mounted) return;
        
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          const profileFromMetadata = {
            first_name: session.user.user_metadata?.first_name,
            last_name: session.user.user_metadata?.last_name
          };
          
          const [role, dbProfile] = await Promise.all([
            fetchUserRole(session.user.id),
            fetchUserProfile(session.user.id)
          ]);
          if (mounted) {
            setUserRole(role);
            setProfile(dbProfile || profileFromMetadata);
          }
        } else {
          setUserRole(null);
          setProfile(null);
        }
        
        if (mounted) setLoading(false);
      } catch (error) {
        console.error('Error initializing auth:', error);
        if (mounted) setLoading(false);
      }
    };

    initializeAuth();

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // Computed properties
  const isAdmin = userRole === 'admin_nocodeo' || userRole === 'admin_entreprise';
  const isLearner = userRole === 'apprenant_particulier';
  const isAdminNoCodeo = userRole === 'admin_nocodeo';
  const isAdminEntreprise = userRole === 'admin_entreprise';
  const isFormateur = userRole === 'formateur';
  const canAccessTraining = ['apprenant_particulier', 'admin_entreprise', 'formateur', 'admin_nocodeo'].includes(userRole || '');

  const value = {
    user,
    session,
    userRole,
    profile,
    loading,
    signUp: (email: string, password: string, role?: UserRole, metadata?: Record<string, any>) => 
      authSignUp(email, password, role, metadata, setters),
    signIn: (email: string, password: string) => 
      authSignIn(email, password, setters),
    signOut: () => authSignOut(setters),
    isAdmin,
    isLearner,
    isAdminNoCodeo,
    isAdminEntreprise,
    isFormateur,
    canAccessTraining,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
